import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, TrendingUp, Zap, Users, Smartphone, Star, ArrowRight } from 'lucide-react';

const FeaturesSection = () => {
  const possibilities = [
    {
      icon: <Heart className="h-12 w-12 text-red-500" />,
      title: "Kunden, die dich lieben",
      subtitle: "Kundenbindung neu definiert",
      story: "Stell dir vor, deine Kunden öffnen deine App und lächeln. Sie freuen sich auf die Benachrichtigungen, teilen deine Inhalte und empfehlen dich weiter. Das ist die Macht einer gut gemachten App.",
      examples: [
        "Personalisierte Angebote zur richtigen Zeit",
        "Loyalty-Programme, die Spaß machen",
        "Direkter Draht zu deinen besten Kunden",
        "Bewertungen und Feedback in Echtzeit"
      ]
    },
    {
      icon: <TrendingUp className="h-12 w-12 text-green-500" />,
      title: "Umsatz, der von alleine wächst",
      subtitle: "Verkaufen im Schlaf",
      story: "Während du schläfst, kaufen deine Kunden über die App ein. Einfacher, schneller und öfter als je zuvor. Das ist der Traum jedes Unternehmers.",
      examples: [
        "One-Click-Bestellungen für Stammkunden",
        "Automatische Nachbestellungen",
        "Location-based Angebote",
        "Social Shopping mit Freunden"
      ]
    },
    {
      icon: <Zap className="h-12 w-12 text-yellow-500" />,
      title: "Prozesse, die wie Magie funktionieren",
      subtitle: "Effizienz auf einem neuen Level",
      story: "Deine Mitarbeiter scannen einen QR-Code und alle Daten sind erfasst. Kein Papierkram, keine Doppeleingaben. Einfach effizient.",
      examples: [
        "Digitale Zeiterfassung per Fingerabdruck",
        "Inventar-Management mit Kamera",
        "Außendienst-Reports in Sekunden",
        "Automatische Synchronisation aller Daten"
      ]
    },
    {
      icon: <Users className="h-12 w-12 text-blue-500" />,
      title: "Teams, die zusammenwachsen",
      subtitle: "Kommunikation ohne Grenzen",
      story: "Egal ob im Büro, im Homeoffice oder beim Kunden - dein Team ist immer verbunden und arbeitet wie ein eingespieltes Orchester.",
      examples: [
        "Projekt-Updates in Echtzeit",
        "Geteilte Kalender und Aufgaben",
        "Instant Messaging mit Kontext",
        "Wissensaustausch leicht gemacht"
      ]
    }
  ];

  return (
    <section id="mobile-possibilities" className="py-16 md:py-24 bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Was wird <span className="text-blue-600">möglich</span>, wenn du eine App hast?
          </h2>
          <p className="text-lg text-gray-600">
            Eine App ist mehr als nur Software - sie kann ein Gamechanger für dein Business sein. 
            Hier sind realistische Möglichkeiten, die wir schon umgesetzt haben.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {possibilities.map((possibility, index) => (
            <Card key={index} className="border-0 shadow-lg bg-white h-full hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8 h-full flex flex-col">
                <div className="flex-shrink-0 mb-6 group-hover:scale-110 transition-transform duration-300">
                  {possibility.icon}
                </div>
                
                <h3 className="text-xl font-bold mb-2">{possibility.title}</h3>
                <p className="text-blue-600 font-semibold mb-4">{possibility.subtitle}</p>
                
                <p className="text-gray-700 mb-6 leading-relaxed italic flex-grow">
                  "{possibility.story}"
                </p>
                
                <div className="mt-auto">
                  <h4 className="font-semibold mb-3 text-gray-900">Das wird möglich:</h4>
                  <ul className="space-y-2">
                    {possibility.examples.map((example, exampleIndex) => (
                      <li key={exampleIndex} className="flex items-start text-sm">
                        <span className="text-blue-500 mr-2 mt-1">✨</span>
                        <span className="text-gray-600">{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Inspiration CTA */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl p-8 max-w-4xl mx-auto mt-16 text-center">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">
            Welche Geschichte wird deine App erzählen?
          </h3>
          <p className="text-lg text-gray-600 mb-8">
            Jede App beginnt mit einer Idee. Was ist deine? 
            Lass uns gemeinsam herausfinden, was realistisch möglich ist.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2">💡 Idee verstehen</h4>
              <p className="text-gray-700 text-sm">Gemeinsam finden wir heraus, was möglich ist</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2">🎨 Erlebnis gestalten</h4>
              <p className="text-gray-700 text-sm">Wir designen eine App, die funktioniert</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2">🚀 Vision verwirklichen</h4>
              <p className="text-gray-700 text-sm">Deine Vision wird mobile Realität</p>
            </div>
          </div>
          
          <a 
            href="#contact" 
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
          >
            Meine App-Idee besprechen
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection; 